/**
 * Knowing what is saved (P-29, #111; Jason, 2026-10-27: "anywhere settings are saved asynchonsly you need to show
 * something, or we should just default to a save button so we know what's actually happening"; design study 28, A):
 * every form with a Save button keeps it, and while it has changes not saved one bar holds to the window's foot naming
 * them, with Discard and Save, and leaving the page asks first. Pure, as src/saved.ts is: each form tells this store
 * what it has not saved (`components/useUnsaved.ts`), the portal's `UnsavedChanges` draws the bar (ui.tsx `UnsavedBar`)
 * for the form changed last and asks before leaving (ui.tsx `LeaveQuestion`).
 */

/** What a form has not saved, and its own Save and Discard. */
export interface UnsavedForm {
  /** The fields changed, by the labels on screen; none, and the form has nothing to save. */
  names: readonly string[];
  /** Exactly what the form's own Save does: the same request, refusals and notice. */
  save: () => void;
  /** The fields put back as they were loaded. */
  discard: () => void;
  busy?: boolean;
  /** False while the form's own Save is held back (a field it cannot save blank). */
  canSave?: boolean;
  /** The form's own button's words, where Save would be wrong ("Propose it"). */
  saveWords?: string;
  /** In place of "Changes not saved", where that would be wrong ("Not proposed yet"). */
  lead?: string;
  /** The form's refusal, read on the bar too, where its Save was pressed. */
  error?: string | null;
}

export interface UnsavedState {
  /** The form the bar names: the one whose changes changed last. */
  shown: (UnsavedForm & { id: string }) | null;
  /** Every form's changes, for the question before leaving. */
  names: string[];
  /** The question before leaving is open. */
  asking: boolean;
}

export interface UnsavedStore {
  /** A form's changes now, or null when it has gone (unmounted). */
  set: (id: string, form: UnsavedForm | null) => void;
  any: () => boolean;
  current: () => UnsavedState;
  /** Asked before leaving: true at once with nothing unsaved; otherwise the question opens, and its answer resolves it. */
  ask: () => Promise<boolean>;
  /** The question's answer: leave without saving, or stay. */
  answer: (leave: boolean) => void;
  /** Go somewhere a form would go with, once asked if anything is not saved (closing a step's panel). */
  afterAsking: (go: () => void) => void;
  subscribe: (listener: () => void) => () => void;
}

const sameForm = (a: UnsavedForm, b: UnsavedForm) =>
  a.names.join('\u0000') === b.names.join('\u0000') &&
  a.save === b.save &&
  a.discard === b.discard &&
  Boolean(a.busy) === Boolean(b.busy) &&
  (a.canSave ?? true) === (b.canSave ?? true) &&
  a.saveWords === b.saveWords &&
  a.lead === b.lead &&
  (a.error ?? null) === (b.error ?? null);

export function createUnsaved(): UnsavedStore {
  const forms = new Map<string, { form: UnsavedForm; at: number }>();
  let tick = 0;
  let question: { promise: Promise<boolean>; resolve: (leave: boolean) => void } | null = null;
  let state: UnsavedState = { shown: null, names: [], asking: false };
  const listeners = new Set<() => void>();
  const changed = () => {
    let shown: UnsavedState['shown'] = null;
    let at = -1;
    const names: string[] = [];
    for (const [id, e] of forms) {
      if (!e.form.names.length) continue;
      for (const n of e.form.names) if (!names.includes(n)) names.push(n);
      if (e.at > at) {
        at = e.at;
        shown = { ...e.form, id };
      }
    }
    // The question's names in the order the forms changed, the one the bar shows first.
    if (shown) names.sort((a, b) => Number(!shown.names.includes(a)) - Number(!shown.names.includes(b)));
    state = { shown, names, asking: question !== null };
    for (const l of listeners) l();
  };
  const any = () => [...forms.values()].some((e) => e.form.names.length > 0);
  const store: UnsavedStore = {
    set(id, form) {
      const was = forms.get(id);
      if (!form) {
        if (!was) return;
        forms.delete(id);
        changed();
        return;
      }
      if (was && sameForm(was.form, form)) return;
      // What changed last is what the bar names: a form's changes changing make it the latest.
      const moved = !was || was.form.names.join('\u0000') !== form.names.join('\u0000');
      forms.set(id, { form, at: moved && form.names.length ? ++tick : (was?.at ?? ++tick) });
      changed();
    },
    any,
    current: () => state,
    ask() {
      if (!any()) return Promise.resolve(true);
      if (question) return question.promise;
      let resolve!: (leave: boolean) => void;
      const promise = new Promise<boolean>((r) => (resolve = r));
      question = { promise, resolve };
      changed();
      return promise;
    },
    answer(leave) {
      const q = question;
      if (!q) return;
      question = null;
      changed();
      q.resolve(leave);
    },
    afterAsking(go) {
      if (!any()) return go();
      void store.ask().then((leave) => leave && go());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
  return store;
}

/** The portal's one record of what is not saved. */
export const unsaved = createUnsaved();

/** A value as a form compares it with what was loaded: words trimmed, and blank (nothing, or only spaces) one blank. */
function asSaved(v: unknown): unknown {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  if (v === undefined) return null;
  if (Array.isArray(v)) return v.map(asSaved);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, x]) => [k, asSaved(x)]),
    );
  }
  return v;
}

/** The same once saved: blank is blank, and spaces round the words are not kept. */
export const sameValue = (a: unknown, b: unknown) => JSON.stringify(asSaved(a)) === JSON.stringify(asSaved(b));

/** A field of a form: its label on screen, and how to read it (one label may cover two values, the site's words and place). */
export type FormField<T> = readonly [label: string, read: (t: T) => unknown];

/** The labels of the fields changed from what was loaded, in the form's order. */
export function changedLabels<T>(was: T, now: T, fields: readonly FormField<T>[]): string[] {
  return fields.filter(([, read]) => !sameValue(read(was), read(now))).map(([label]) => label);
}

/** The names on the bar: up to three, then how many more ("Name, Jurisdiction, Goal and 2 more"). */
export function namesLine(names: readonly string[], max = 3): string {
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
}

interface At {
  pathname: string;
  search: string;
}

/** Leaving asks first: another page, or the same page asked another way (?step=); a mark on it (#status) is staying. */
export const asksBeforeLeaving = (anyUnsaved: boolean, from: At, to: At) => anyUnsaved && (from.pathname !== to.pathname || from.search !== to.search);

/** Closing or reloading the tab asks while anything is not saved: the browser's own question (its words are its own). */
export function holdUnload(e: { preventDefault: () => void; returnValue: unknown }, anyUnsaved: boolean) {
  if (!anyUnsaved) return;
  e.preventDefault();
  // Older browsers ask only when this is set.
  e.returnValue = '';
}
